import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobButton,
  KoobInputDate,
  KoobInputDateRange,
  KoobInputStepper,
  icons,
} from '@koob/ui';
import {
  DATE_LONG,
  dayjs,
  formatDate,
  parseDateOnly,
  toDateString,
} from '@koob/dates';
import {
  MAX_ADULTS_WITHOUT_ROOMS,
  splitRooms,
  suggestedRooms,
} from './searchRooms';

// A date the model gave, kept only when it is today or later.
const futureDate = value => {
  const date = value && parseDateOnly(value);
  return date && !dayjs(date).isBefore(dayjs(), 'day') ? date : null;
};

/**
 * KD-3848 — the koob:availability block: searches the booking module from
 * the chat. The model gives what the TO said (dates, adults, children);
 * the form asks for the rest. Children always need their birthdates, which
 * no question gives. With nothing missing, it searches at once (only as the
 * answer arrives, not when a conversation is reopened) and shows a summary.
 * A hotel takes check-in and check-out, an experience its start date (the
 * page adds its duration).
 * Compact on purpose: the fields a search needs. A hotel's guests are spread
 * over its rooms (searchRooms.js); a set split per room goes through the
 * booking module's own form.
 */
export default function AvailabilitySearch({ data, fresh, actions }) {
  const { t } = useTranslation('assistant');
  const isHotel = actions.productType === 'hotel';
  const [startAt, setStartAt] = useState(() => futureDate(data.startAt));
  const [endAt, setEndAt] = useState(() =>
    isHotel ? futureDate(data.endAt) : null,
  );
  const [adults, setAdults] = useState(data.adults ?? 2);
  const [birthdates, setBirthdates] = useState(() =>
    Array(data.children ?? 0).fill(null),
  );
  const children = birthdates.length;
  // A hotel group the TO gave no room count for: a guess, which they confirm.
  const groupWithoutRooms =
    isHotel && !data.rooms && adults > MAX_ADULTS_WITHOUT_ROOMS;
  const [rooms, setRooms] = useState(() =>
    Math.min(
      data.rooms ?? (groupWithoutRooms ? suggestedRooms(adults) : 1),
      adults,
    ),
  );

  const datesReady = isHotel
    ? startAt && endAt && dayjs(endAt).isAfter(startAt, 'day')
    : !!startAt;
  const ready = datesReady && birthdates.every(Boolean);
  // The form opens on what is missing, adults included: 2 is only a default.
  const [editing, setEditing] = useState(
    !(ready && data.adults && !groupWithoutRooms),
  );
  const [searched, setSearched] = useState(false);

  const search = () => {
    actions.searchAvailability({
      composition: isHotel
        ? splitRooms(rooms, adults, birthdates)
        : [{ adults, children, childrenBirthdates: birthdates }],
      startAt: toDateString(startAt),
      endAt: isHotel ? toDateString(endAt) : null,
    });
    setEditing(false);
    setSearched(true);
  };

  // Nothing to ask: no click needed. Once, when the block appears.
  useEffect(() => {
    if (fresh && !editing) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button = searched ? (
    // The results are on the page: point to them.
    <KoobButton size="sm" onClick={() => actions.showSection('booking')}>
      <i className={icons.booking} />
      {t('blocks.showResults')}
    </KoobButton>
  ) : (
    <KoobButton size="sm" onClick={search} disabled={!ready}>
      <i className={icons.search} />
      {t('blocks.searchAvailability')}
    </KoobButton>
  );

  if (!editing) {
    const travellers = [
      t('blocks.adults', { count: adults }),
      children > 0 && t('blocks.children', { count: children }),
      rooms > 1 && t('blocks.rooms', { count: rooms }),
    ]
      .filter(Boolean)
      .join(', ');
    const dates = isHotel
      ? `${formatDate(startAt, DATE_LONG)} – ${formatDate(endAt, DATE_LONG)}`
      : formatDate(startAt, DATE_LONG);
    return (
      <div className="flex flex-col gap-2.5 rounded-lg border border-solid border-gray-200 p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0">
            {dates} · {travellers}
          </span>
          <KoobButton
            size="sm"
            variant="subtle"
            onClick={() => {
              setEditing(true);
              setSearched(false);
            }}
          >
            {t('blocks.change')}
          </KoobButton>
        </div>
        <div>{button}</div>
      </div>
    );
  }

  const counter = (label, value, min, onChange, max = 20) => (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      {label}
      <KoobInputStepper
        size="sm"
        value={value}
        min={min}
        max={max}
        onValueChange={onChange}
        aria-label={label}
      />
    </label>
  );

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-solid border-gray-200 p-3 text-sm">
      {isHotel ? (
        <KoobInputDateRange
          block
          size="sm"
          presets={[]}
          numberOfMonths={1}
          placeholder={t('blocks.datesPlaceholder')}
          value={{ from: startAt ?? undefined, to: endAt ?? undefined }}
          onChange={range => {
            setStartAt(range?.from ?? null);
            setEndAt(range?.to ?? null);
          }}
          minDate={new Date()}
        />
      ) : (
        <KoobInputDate
          block
          size="sm"
          placeholder={t('blocks.startDate')}
          value={startAt}
          onChange={setStartAt}
          minDate={new Date()}
        />
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {counter(t('blocks.adultsLabel'), adults, 1, count => {
          setAdults(count);
          // Each room needs an adult.
          setRooms(current => Math.min(current, count));
        })}
        {counter(t('blocks.childrenLabel'), children, 0, count =>
          setBirthdates(current =>
            Array.from({ length: count }, (_, i) => current[i] ?? null),
          ),
        )}
        {isHotel && counter(t('blocks.roomsLabel'), rooms, 1, setRooms, adults)}
      </div>
      {children > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {birthdates.map((date, i) => (
            <KoobInputDate
              key={i}
              block
              size="sm"
              placeholder={t('blocks.childBirthdate', { number: i + 1 })}
              value={date}
              onChange={value =>
                setBirthdates(current =>
                  current.map((d, j) => (j === i ? value : d)),
                )
              }
              maxDate={new Date()}
            />
          ))}
        </div>
      )}
      <div>{button}</div>
    </div>
  );
}
